import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  ViewChild,
  HostListener,
} from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { WhatsAppAdminService } from '../../services/whatsapp-admin.service';
import {
  WhatsAppConnectionStatus,
  RateLimitStatusResponse,
  OTPStatsResponse,
  OTPItem,
  OTPStatusType,
  OTPPurpose,
  WhatsAppNumber,
  WhatsAppNumberRole,
  WhatsAppNumberStatus,
  BASE_WHATSAPP_NUMBER_ID,
} from '../../interfaces/whatsapp-admin.interface';
import {
  BehaviorSubject,
  Observable,
  Subject,
  forkJoin,
  merge,
  of,
  timer,
} from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  exhaustMap,
  map,
  switchMap,
  takeUntil,
} from 'rxjs/operators';
import Swal from 'sweetalert2';

/** Estados en los que un número está "en camino" y hay que mirarlo de cerca. */
const TRANSITIONAL_STATUSES: WhatsAppConnectionStatus[] = ['CONNECTING', 'QR_READY'];

const MAX_LABEL_LENGTH = 60;

@Component({
  selector: 'app-admin-whatsapp-otp',
  templateUrl: './admin-whatsapp-otp.component.html',
  styleUrls: ['./admin-whatsapp-otp.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminWhatsappOtpComponent implements OnInit, OnDestroy {
  // Números de WhatsApp: el base (registro) primero y después los extras
  // (pagadores). El qr solo viene lleno para los que están en QR_READY.
  numbers: WhatsAppNumberStatus[] = [];
  numbersLoaded = false;
  loading = false;

  // Polling: rápido mientras algún número está conectando o esperando QR
  // (para mostrar el QR y enterarse del escaneo), lento cuando todo está
  // estable, para que una caída con el panel abierto igual se vea.
  private readonly FAST_POLL_MS = 3000;
  private readonly SLOW_POLL_MS = 30000;
  // Después de conectar/agregar, el backend puede tardar un instante en pasar
  // a CONNECTING: sin esta ventana el polling podría volver a lento y el QR
  // aparecería recién 30s después.
  private readonly FAST_AFTER_ACTION_MS = 15000;
  private fastPollUntil = 0;
  private pollInterval$ = new BehaviorSubject<number>(this.FAST_POLL_MS);
  private refreshNumbers$ = new Subject<void>();
  // Se incrementa cada vez que una acción del operador escribe el estado
  // local. Un tick del polling que salió antes de la acción trae datos viejos
  // y se descarta, para que no "deshaga" en pantalla lo que se acaba de hacer.
  private stateVersion = 0;
  private pollErrorShown = false;
  private destroy$ = new Subject<void>();

  // Rate Limit
  rateLimit: RateLimitStatusResponse | null = null;

  // Estadísticas
  stats: OTPStatsResponse | null = null;

  // Mensajes/OTPs
  messages: OTPItem[] = [];
  totalMessages = 0;
  pageSize = 10;
  currentPage = 1;
  filterStatus: OTPStatusType | '' = '';
  filterPhone = '';
  loadingMessages = false;

  readonly messageColumns = [
    'otpId',
    'phoneNumber',
    'status',
    'purpose',
    'sender',
    'attempts',
    'createdAt',
    'expiresAt',
  ];

  // Test OTP
  testPhoneNumber = '';
  /** Número por el que sale la prueba: el base o un extra de pagadores. */
  testNumberId: string = BASE_WHATSAPP_NUMBER_ID;
  /** Nombre de cliente de ejemplo; solo aplica a los extras (marca blanca). */
  testSenderName = '';
  sendingTestOTP = false;
  showCountryDropdown = false;
  selectedCountryCode = '+56';
  selectedCountryFlag = 'chile.png';

  countryPhoneOptions = [
    { code: '+56', name: 'Chile', flag: 'chile.png' },
    { code: '+58', name: 'Venezuela', flag: 'venezuela.png' },
    { code: '+54', name: 'Argentina', flag: 'argentina.png' },
    { code: '+55', name: 'Brasil', flag: 'brasil.png' },
    { code: '+57', name: 'Colombia', flag: 'colombia.png' },
    { code: '+593', name: 'Ecuador', flag: 'ecuador.png' },
    { code: '+595', name: 'Paraguay', flag: 'paraguay.png' },
    { code: '+51', name: 'Perú', flag: 'peru.png' },
    { code: '+598', name: 'Uruguay', flag: 'uruguay.png' },
    { code: '+1', name: 'Estados Unidos', flag: 'usa.png' },
  ];

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  statusOptions: { value: OTPStatusType | ''; label: string }[] = [
    { value: '', label: 'Todos' },
    { value: 'PENDING', label: 'Pendiente' },
    { value: 'VERIFIED', label: 'Verificado' },
    { value: 'EXPIRED', label: 'Expirado' },
    { value: 'BLOCKED', label: 'Bloqueado' },
  ];

  constructor(
    private whatsappService: WhatsAppAdminService,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.whatsappService.loading$
      .pipe(takeUntil(this.destroy$))
      .subscribe((isLoading) => {
        this.loading = isLoading;
        this.cdr.markForCheck();
      });

    this.startNumbersPolling();
    // Estadísticas, rate limit e historial son globales: se muestran aunque
    // ningún número esté conectado (sirven justamente para diagnosticar).
    this.loadGlobalData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // --- Números y polling ---

  private startNumbersPolling(): void {
    const ticks$ = this.pollInterval$.pipe(
      distinctUntilChanged(),
      switchMap((ms) => timer(0, ms)),
    );

    merge(ticks$, this.refreshNumbers$)
      .pipe(
        // exhaustMap: si el backend tarda más que el intervalo, no se apilan
        // requests; el tick siguiente espera a que termine el anterior.
        exhaustMap(() => {
          const version = this.stateVersion;
          return this.fetchNumbers().pipe(
            map((numbers) => ({ numbers, version })),
            // catchError por tick: un error transitorio (red, deploy) no debe
            // matar el polling; se avisa una vez y se sigue intentando.
            catchError((err) => {
              if (!this.pollErrorShown) {
                this.pollErrorShown = true;
                this.showErrorMessage(
                  err?.message || 'Error al obtener los números de WhatsApp',
                );
              }
              this.numbersLoaded = true;
              this.cdr.markForCheck();
              return of(null);
            }),
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((result) => {
        if (!result) return;
        this.pollErrorShown = false;
        // Salió antes de una acción del operador: datos viejos. El estado
        // local ya refleja la acción; el próximo tick trae el real.
        if (result.version !== this.stateVersion) return;
        this.applyNumbers(result.numbers);
      });
  }

  /**
   * GET numbers y, para los que están conectando o esperando QR, GET
   * numbers/:id, que es el único que trae el QR.
   */
  private fetchNumbers(): Observable<WhatsAppNumberStatus[]> {
    return this.whatsappService.getNumbers().pipe(
      switchMap((list) => {
        const withQr = list.map((n) =>
          this.isTransitional(n.status)
            ? this.whatsappService.getNumber(n.id).pipe(
                // Si falla el detalle de uno, se muestra lo de la lista (sin
                // QR) en vez de perder el tick entero.
                catchError(() => of(this.withoutQr(n))),
              )
            : of(this.withoutQr(n)),
        );
        return withQr.length ? forkJoin(withQr) : of([]);
      }),
    );
  }

  private applyNumbers(next: WhatsAppNumberStatus[]): void {
    if (this.numbersLoaded) {
      this.notifyTransitions(this.numbers, next);
    }
    this.numbers = next;
    this.numbersLoaded = true;
    this.updatePollInterval();
    this.cdr.markForCheck();
  }

  /**
   * Avisa los cambios de estado que el operador no provocó desde esta
   * pantalla: el escaneo del QR (se conecta) y, sobre todo, una caída.
   */
  private notifyTransitions(
    prev: WhatsAppNumberStatus[],
    next: WhatsAppNumberStatus[],
  ): void {
    let rateLimitDirty = false;
    for (const n of next) {
      const before = prev.find((p) => p.id === n.id);
      if (!before || before.status === n.status) continue;
      if (n.status === 'CONNECTED') {
        this.showSuccessMessage(`${n.label}: WhatsApp conectado exitosamente`);
        rateLimitDirty = true;
      } else if (before.status === 'CONNECTED') {
        this.showErrorMessage(
          n.role === 'registro'
            ? `${n.label} se desconectó: los OTP de registro no se están enviando`
            : `${n.label} se desconectó`,
        );
        rateLimitDirty = true;
      }
    }
    if (rateLimitDirty) this.loadRateLimit();
  }

  private updatePollInterval(): void {
    const fast =
      this.numbers.some((n) => this.isTransitional(n.status)) ||
      Date.now() < this.fastPollUntil;
    this.pollInterval$.next(fast ? this.FAST_POLL_MS : this.SLOW_POLL_MS);
  }

  /** Escribe en pantalla el resultado de una acción y ajusta el polling. */
  private upsertNumber(updated: WhatsAppNumberStatus): void {
    this.stateVersion++;
    const idx = this.numbers.findIndex((n) => n.id === updated.id);
    this.numbers =
      idx === -1
        ? [...this.numbers, updated]
        : this.numbers.map((n, i) => (i === idx ? updated : n));
    this.updatePollInterval();
    this.cdr.markForCheck();
  }

  private pollFastForAWhile(): void {
    this.fastPollUntil = Date.now() + this.FAST_AFTER_ACTION_MS;
    this.updatePollInterval();
  }

  private withoutQr(n: WhatsAppNumber): WhatsAppNumberStatus {
    return { ...n, qr: null };
  }

  isTransitional(status: WhatsAppConnectionStatus): boolean {
    return TRANSITIONAL_STATUSES.includes(status);
  }

  trackByNumberId(_index: number, n: WhatsAppNumber): string {
    return n.id;
  }

  // --- Acciones por número ---

  onConnect(n: WhatsAppNumberStatus): void {
    this.whatsappService.connectNumber(n.id).subscribe({
      next: (status) => {
        this.upsertNumber(status);
        this.pollFastForAWhile();
        this.showSuccessMessage(
          n.status === 'QR_READY'
            ? `${n.label}: se pidió un QR nuevo`
            : `${n.label}: conexión iniciada`,
        );
      },
      error: (err) => {
        this.showErrorMessage(
          err?.message || 'Error al iniciar la conexión de WhatsApp',
        );
        this.cdr.markForCheck();
      },
    });
  }

  /** "Cancelar": detiene la conexión o la espera del QR, sin borrar la sesión. */
  onStop(n: WhatsAppNumberStatus): void {
    this.whatsappService.stopNumber(n.id).subscribe({
      next: (status) => {
        this.fastPollUntil = 0;
        this.upsertNumber(status);
        this.showSuccessMessage(`${n.label}: conexión detenida`);
      },
      error: (err) => {
        this.showErrorMessage(err?.message || 'Error al detener la conexión');
        this.cdr.markForCheck();
      },
    });
  }

  /** Cierra la sesión en WhatsApp y borra las credenciales guardadas. */
  async onDisconnect(n: WhatsAppNumberStatus): Promise<void> {
    const isBase = n.role === 'registro';
    const result = await Swal.fire({
      // titleText/text en vez de title/html: la etiqueta la escribe un
      // operador y no debe interpretarse como HTML.
      titleText: `¿Desconectar ${n.label}?`,
      text: isBase
        ? 'Es el número de REGISTRO de Aguacate: mientras esté desconectado ' +
          'ningún usuario nuevo va a recibir su código OTP. Se cierra la ' +
          'sesión de WhatsApp y se borran las credenciales guardadas, así ' +
          'que para volver a conectarlo hay que escanear un QR nuevo.'
        : 'Se cierra la sesión de WhatsApp y se borran las credenciales ' +
          'guardadas: para volver a conectarlo hay que escanear un QR nuevo. ' +
          'El número queda en la lista.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: isBase
        ? 'Sí, desconectar el de registro'
        : 'Sí, desconectar',
      cancelButtonText: 'Cancelar',
      reverseButtons: true,
      focusCancel: true,
    });

    if (!result.isConfirmed) return;

    this.whatsappService.disconnectNumber(n.id).subscribe({
      next: (response) => {
        this.fastPollUntil = 0;
        this.upsertNumber({
          ...n,
          status: 'DISCONNECTED',
          qr: null,
          connectedNumber: null,
          connectedName: null,
          hasStoredCredentials: false,
        });
        this.refreshNumbers$.next();
        this.showSuccessMessage(response?.message || 'Número desconectado');
      },
      error: (err) => {
        this.showErrorMessage(err?.message || 'Error al desconectar WhatsApp');
        this.cdr.markForCheck();
      },
    });
  }

  /** Quita un número extra del pool (el base no se puede quitar). */
  async onRemove(n: WhatsAppNumberStatus): Promise<void> {
    if (n.role !== 'pagador') return;

    const result = await Swal.fire({
      titleText: `¿Quitar ${n.label}?`,
      text:
        'Se cierra su sesión de WhatsApp y deja de usarse para los OTP de ' +
        'pagadores. Para volver a usarlo habría que agregarlo y escanear el ' +
        'QR de nuevo.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sí, quitar',
      cancelButtonText: 'Cancelar',
      reverseButtons: true,
      focusCancel: true,
    });

    if (!result.isConfirmed) return;

    this.whatsappService.removeNumber(n.id).subscribe({
      next: (response) => {
        this.stateVersion++;
        this.numbers = this.numbers.filter((x) => x.id !== n.id);
        this.updatePollInterval();
        this.showSuccessMessage(response?.message || 'Número quitado');
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.showErrorMessage(err?.message || 'Error al quitar el número');
        this.cdr.markForCheck();
      },
    });
  }

  async onAddNumber(): Promise<void> {
    const result = await Swal.fire<string>({
      title: 'Agregar número de WhatsApp',
      text:
        'Se usará solo para los OTP de pagadores (marca blanca). Después de ' +
        'agregarlo, escanea el QR con el WhatsApp de ese teléfono.',
      input: 'text',
      inputLabel: 'Etiqueta',
      inputPlaceholder: 'Ej: Pagadores 2',
      inputAttributes: { maxlength: String(MAX_LABEL_LENGTH) },
      inputValidator: (value) => {
        const label = (value ?? '').trim();
        if (!label) return 'Ingresa una etiqueta para identificar el número';
        if (label.length > MAX_LABEL_LENGTH) {
          return `Máximo ${MAX_LABEL_LENGTH} caracteres`;
        }
        return null;
      },
      showCancelButton: true,
      confirmButtonColor: '#1C593D',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Agregar',
      cancelButtonText: 'Cancelar',
      reverseButtons: true,
    });

    if (!result.isConfirmed || !result.value) return;

    this.whatsappService.addNumber(result.value.trim()).subscribe({
      next: (created) => {
        this.upsertNumber(this.withoutQr(created));
        // El backend ya arrancó la conexión: el polling rápido trae el QR
        // (GET numbers/:id) en cuanto esté listo.
        this.pollFastForAWhile();
        this.refreshNumbers$.next();
        this.showSuccessMessage(
          `${created.label} agregado. En unos segundos aparece su QR.`,
        );
      },
      error: (err) => {
        this.showErrorMessage(err?.message || 'Error al agregar el número');
        this.cdr.markForCheck();
      },
    });
  }

  // --- Datos globales ---

  private loadGlobalData(): void {
    this.loadStats();
    this.loadRateLimit();
    this.loadMessages();
  }

  loadStats(): void {
    this.whatsappService.getOTPStats().subscribe({
      next: (data: OTPStatsResponse) => {
        this.stats = data;
        this.cdr.markForCheck();
      },
      error: () => {
        this.stats = null;
        this.cdr.markForCheck();
      },
    });
  }

  loadRateLimit(): void {
    this.whatsappService.getRateLimitStatus().subscribe({
      next: (data: RateLimitStatusResponse) => {
        this.rateLimit = data;
        this.cdr.markForCheck();
      },
      error: () => {
        this.rateLimit = null;
        this.cdr.markForCheck();
      },
    });
  }

  loadMessages(): void {
    this.loadingMessages = true;
    this.cdr.markForCheck();

    this.whatsappService
      .getOTPMessages({
        page: this.currentPage,
        limit: this.pageSize,
        status: this.filterStatus || undefined,
        phoneNumber: this.filterPhone || undefined,
      })
      .subscribe({
        next: (data) => {
          this.messages = data.items;
          this.totalMessages = data.total;
          this.loadingMessages = false;
          this.cdr.markForCheck();
        },
        error: (err) => {
          this.loadingMessages = false;
          this.showErrorMessage(
            err?.message || 'Error al obtener el historial de mensajes',
          );
          this.cdr.markForCheck();
        },
      });
  }

  onPageChange(event: PageEvent): void {
    this.currentPage = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    this.loadMessages();
  }

  onFilterChange(): void {
    this.currentPage = 1;
    if (this.paginator) {
      this.paginator.firstPage();
    }
    this.loadMessages();
  }

  async onCleanupCache(): Promise<void> {
    const result = await Swal.fire({
      title: '¿Limpiar cache?',
      text: 'Esto eliminará el cache de números expirados y marcará OTPs expirados.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Sí, limpiar',
      cancelButtonText: 'Cancelar',
      reverseButtons: true,
    });

    if (result.isConfirmed) {
      this.whatsappService.cleanupCache().subscribe({
        next: (response) => {
          Swal.fire({
            title: 'Limpieza completada',
            text: `Se eliminaron ${response.deletedFromCache} registros del cache y ${response.expiredOTPs} OTPs expirados.`,
            icon: 'success',
          });
          this.loadStats();
          this.loadMessages();
          this.cdr.markForCheck();
        },
        error: (err) => {
          this.showErrorMessage(err?.message || 'Error al limpiar el cache');
          this.cdr.markForCheck();
        },
      });
    }
  }

  refreshAll(): void {
    this.refreshNumbers$.next();
    this.loadGlobalData();
  }

  // --- OTP de prueba ---

  selectCountry(code: string, flag: string): void {
    this.selectedCountryCode = code;
    this.selectedCountryFlag = flag;
    this.showCountryDropdown = false;
    this.cdr.markForCheck();
  }

  toggleCountryDropdown(): void {
    this.showCountryDropdown = !this.showCountryDropdown;
    this.cdr.markForCheck();
  }

  getFullPhoneNumber(): string {
    // Remover el + del código y concatenar con el número
    const code = this.selectedCountryCode.replace('+', '');
    return code + this.testPhoneNumber;
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const clickedInside = target.closest('.country-selector');
    if (!clickedInside && this.showCountryDropdown) {
      this.showCountryDropdown = false;
      this.cdr.markForCheck();
    }
  }

  async onSendTestOTP(): Promise<void> {
    if (!this.testPhoneNumber.trim()) {
      this.showErrorMessage('Ingrese un número de teléfono');
      return;
    }

    const fullNumber = this.getFullPhoneNumber();
    const displayNumber = `${this.selectedCountryCode} ${this.testPhoneNumber}`;
    const sender = this.numbers.find((x) => x.id === this.testNumberId);
    if (!sender) {
      // El número elegido se quitó mientras el panel estaba abierto.
      this.testNumberId = BASE_WHATSAPP_NUMBER_ID;
      this.showErrorMessage('El número elegido ya no existe. Elija otro.');
      return;
    }
    const isPayer = sender.role === 'pagador';
    const senderName = isPayer ? this.testSenderName.trim() : '';
    const detail = isPayer
      ? `Sale por "${sender.label}" con el mensaje de pagador${senderName ? ` a nombre de "${senderName}"` : ' sin nombre de cliente'}.`
      : 'Sale por el número de registro.';

    // text en vez de html: el número lo tipea el operador y no se escapa.
    const result = await Swal.fire({
      title: 'Enviar OTP de prueba',
      text: `¿Enviar código OTP al número ${displayNumber}? ${detail}`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Sí, enviar',
      cancelButtonText: 'Cancelar',
      reverseButtons: true,
    });

    if (result.isConfirmed) {
      this.sendingTestOTP = true;
      this.cdr.markForCheck();

      this.whatsappService.sendTestOTP(fullNumber, sender.id, senderName).subscribe({
        next: (response) => {
          this.sendingTestOTP = false;
          if (response.success) {
            const expires = response.expiresInSeconds
              ? ` Vence en ${this.formatSeconds(response.expiresInSeconds)}.`
              : '';
            Swal.fire({
              title: 'OTP Enviado',
              text: `Código enviado exitosamente a ${displayNumber}.${expires}`,
              icon: 'success',
            });
            this.testPhoneNumber = '';
          } else {
            // En COOLDOWN el backend dice cuánto falta para poder reenviar
            const wait = response.resendInSeconds
              ? ` Podrás reenviar en ${this.formatSeconds(response.resendInSeconds)}.`
              : '';
            this.showErrorMessage(`${response.message}${wait}`);
          }
          // Aunque falle, el intento queda en el historial y en las métricas
          this.loadMessages();
          this.loadStats();
          this.loadRateLimit();
          this.cdr.markForCheck();
        },
        error: (err) => {
          this.sendingTestOTP = false;
          this.showErrorMessage(err?.message || 'Error al enviar OTP de prueba');
          this.cdr.markForCheck();
        },
      });
    }
  }

  /** El número elegido para la prueba es de pagadores (marca blanca). */
  isPayerTestSender(): boolean {
    return this.numbers.find((x) => x.id === this.testNumberId)?.role === 'pagador';
  }

  private formatSeconds(total: number): string {
    const s = Math.max(0, Math.round(total));
    if (s < 60) return `${s} s`;
    const min = Math.floor(s / 60);
    const rest = s % 60;
    return rest ? `${min} min ${rest} s` : `${min} min`;
  }

  // --- Helpers de presentación ---

  getStatusLabel(status: WhatsAppConnectionStatus): string {
    const labels: Record<WhatsAppConnectionStatus, string> = {
      DISCONNECTED: 'Desconectado',
      CONNECTING: 'Conectando...',
      CONNECTED: 'Conectado',
      QR_READY: 'Esperando escaneo de QR',
    };
    return labels[status] || status;
  }

  getStatusIcon(status: WhatsAppConnectionStatus): string {
    const icons: Record<WhatsAppConnectionStatus, string> = {
      DISCONNECTED: 'link_off',
      CONNECTING: 'sync',
      CONNECTED: 'check_circle',
      QR_READY: 'qr_code_2',
    };
    return icons[status] || 'help';
  }

  getStatusColor(status: WhatsAppConnectionStatus): string {
    const colors: Record<WhatsAppConnectionStatus, string> = {
      DISCONNECTED: 'text-red-500',
      CONNECTING: 'text-yellow-500',
      CONNECTED: 'text-green-500',
      QR_READY: 'text-blue-500',
    };
    return colors[status] || 'text-gray-500';
  }

  getStatusBadgeClass(status: WhatsAppConnectionStatus): string {
    const classes: Record<WhatsAppConnectionStatus, string> = {
      DISCONNECTED: 'bg-red-100 text-red-700',
      CONNECTING: 'bg-yellow-100 text-yellow-700',
      CONNECTED: 'bg-green-100 text-green-700',
      QR_READY: 'bg-blue-100 text-blue-700',
    };
    return classes[status] || 'bg-gray-100 text-gray-700';
  }

  getRoleLabel(role: WhatsAppNumberRole): string {
    return role === 'registro' ? 'Registro' : 'Pagador';
  }

  getPurposeLabel(purpose: OTPPurpose | null | undefined): string {
    if (purpose === 'registro') return 'Registro';
    if (purpose === 'pagador') return 'Pagador';
    return '—';
  }

  /** Etiqueta del número que envió un OTP, resolviendo el id contra la lista. */
  getSenderLabel(sender: string | null | undefined): string {
    if (!sender) return '—';
    const found = this.numbers.find((n) => n.id === sender);
    if (found) return found.label;
    if (sender === BASE_WHATSAPP_NUMBER_ID) return 'Número de registro';
    // El extra se quitó del pool después de enviar este OTP
    return 'Número quitado';
  }

  /** Desconectar tiene sentido si hay sesión: conectado o con credenciales. */
  canDisconnect(n: WhatsAppNumber): boolean {
    return n.status === 'CONNECTED' || n.hasStoredCredentials;
  }

  getRateLimitPercentage(): number {
    if (!this.rateLimit || !this.rateLimit.maxPerMinute) return 0;
    return Math.min(
      100,
      (this.rateLimit.messagesThisMinute / this.rateLimit.maxPerMinute) * 100,
    );
  }

  getOTPStatusLabel(status: OTPStatusType): string {
    const labels: Record<OTPStatusType, string> = {
      PENDING: 'Pendiente',
      VERIFIED: 'Verificado',
      EXPIRED: 'Expirado',
      BLOCKED: 'Bloqueado',
    };
    return labels[status] || status;
  }

  getOTPStatusClass(status: OTPStatusType): string {
    const classes: Record<OTPStatusType, string> = {
      PENDING: 'bg-yellow-100 text-yellow-700',
      VERIFIED: 'bg-green-100 text-green-700',
      EXPIRED: 'bg-gray-100 text-gray-700',
      BLOCKED: 'bg-red-100 text-red-700',
    };
    return classes[status] || '';
  }

  private showSuccessMessage(message: string): void {
    this.snackBar.open(message, 'Cerrar', {
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['success-snackbar'],
    });
  }

  private showErrorMessage(message: string): void {
    this.snackBar.open(message, 'Cerrar', {
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['error-snackbar'],
    });
  }
}
